import { useState, useCallback, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { useNotifications } from "@/hooks/useNotifications";
import { Mail, Lock, CreditCard } from "lucide-react";
import { GymCodeInput } from "../GymCodeInput";
import { SignUpConfirmDialog } from "./SignUpConfirmDialog";

interface ValidatedGym {
  id: string;
  name: string;
}

interface CustomSignUpFormProps {
  onSwitchToSignIn: () => void;
}

export const CustomSignUpForm = ({ onSwitchToSignIn }: CustomSignUpFormProps) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [confirmPasswordError, setConfirmPasswordError] = useState("");
  const [gymCode, setGymCode] = useState("");
  const [validatedGym, setValidatedGym] = useState<ValidatedGym | null>(null);
  const [dni, setDni] = useState("");
  const [dniError, setDniError] = useState("");
  const [loading, setLoading] = useState(false);
  const [emailError, setEmailError] = useState("");
  const [isCheckingEmail, setIsCheckingEmail] = useState(false);
  const [emailChecked, setEmailChecked] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const { success, error: showError } = useNotifications();
  const emailCheckTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validateDni = (dniValue: string): boolean => {
    if (!validatedGym) return true;
    if (!dniValue.trim()) {
      setDniError("El DNI es obligatorio cuando ingresas un código de gimnasio");
      return false;
    }
    if (dniValue.length < 7 || dniValue.length > 15) {
      setDniError("El DNI debe tener entre 7 y 15 caracteres");
      return false;
    }
    if (!/^\d+$/.test(dniValue)) {
      setDniError("El DNI solo puede contener números");
      return false;
    }
    setDniError("");
    return true;
  };

  const handleDniChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 15);
    setDni(value);
    if (dniError) {
      validateDni(value);
    }
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setPassword(value);
    if (confirmPassword && value !== confirmPassword) {
      setConfirmPasswordError("Las contraseñas no coinciden");
    } else {
      setConfirmPasswordError("");
    }
  };

  const handleConfirmPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setConfirmPassword(value);
    if (value && password && value !== password) {
      setConfirmPasswordError("Las contraseñas no coinciden");
    } else {
      setConfirmPasswordError("");
    }
  };

  const checkEmailExists = async (emailToCheck: string) => {
    if (!validateEmail(emailToCheck)) return;

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    setIsCheckingEmail(true);
    setEmailChecked(false);

    try {
      const { data: existingProfile, error: profileError } = await supabase
        .from('profiles')
        .select('id, username')
        .eq('username', emailToCheck.toLowerCase())
        .maybeSingle();

      if (profileError) {
        setEmailError("Error al verificar email. Intenta nuevamente.");
        return;
      }

      if (existingProfile) {
        setEmailError("Este correo ya se encuentra registrado");
      } else {
        setEmailError("");
      }
      setEmailChecked(true);
    } catch (error: any) {
      if (error.name === 'AbortError') return;
      setEmailError("Error al verificar email. Puedes continuar pero verifica que el email sea correcto.");
    } finally {
      if (!abortControllerRef.current?.signal.aborted) {
        setIsCheckingEmail(false);
      }
    }
  };

  const handleEmailChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const newEmail = e.target.value;
    setEmail(newEmail);
    setEmailError("");
    setEmailChecked(false);

    if (emailCheckTimeoutRef.current) {
      clearTimeout(emailCheckTimeoutRef.current);
    }

    if (newEmail.trim() && validateEmail(newEmail)) {
      emailCheckTimeoutRef.current = setTimeout(() => {
        checkEmailExists(newEmail);
      }, 500);
    }
  }, []);

  useEffect(() => {
    return () => {
      if (emailCheckTimeoutRef.current) {
        clearTimeout(emailCheckTimeoutRef.current);
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  useEffect(() => {
    if (!validatedGym) {
      setDniError("");
    }
  }, [validatedGym]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateEmail(email)) {
      setEmailError("Por favor ingresa un email válido");
      return;
    }

    if (password.length < 6) {
      showError("La contraseña debe tener al menos 6 caracteres");
      return;
    }

    if (password !== confirmPassword) {
      setConfirmPasswordError("Las contraseñas no coinciden");
      return;
    }

    if (validatedGym && !validateDni(dni)) {
      return;
    }

    if (!emailChecked) {
      await checkEmailExists(email);
    }
    if (emailError) {
      return;
    }

    // Todo OK → mostrar modal de confirmación
    setShowConfirmModal(true);
  };

  const handleConfirmRegistration = async () => {
    setLoading(true);

    try {
      const { data: signUpData, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: `${window.location.origin}/email-confirmed`,
          data: {
            gym_id: validatedGym?.id || null,
            dni: validatedGym ? dni : null
          }
        }
      });

      if (error) {
        if (error.message.includes("User already registered") ||
            error.message.includes("already been registered") ||
            error.message.includes("email already taken") ||
            error.message.includes("email address is already registered")) {
          setEmailError("Este correo ya se encuentra registrado");
          setShowConfirmModal(false);
        } else {
          showError(error.message);
        }
      } else {
        if (signUpData?.user && !signUpData.user.email_confirmed_at && signUpData.user.created_at) {
          const createdAt = new Date(signUpData.user.created_at);
          const now = new Date();
          const timeDiff = now.getTime() - createdAt.getTime();

          if (timeDiff < 10000) {
            success("¡Registro exitoso! Revisa tu email para verificar tu cuenta.");
            resetForm();
          } else {
            setEmailError("Este correo ya se encuentra registrado");
            setShowConfirmModal(false);
          }
        } else if (signUpData?.user?.email_confirmed_at) {
          setEmailError("Este correo ya se encuentra registrado");
          setShowConfirmModal(false);
        } else {
          success("¡Registro exitoso! Revisa tu email para verificar tu cuenta.");
          resetForm();
        }
      }
    } catch (error) {
      console.error("Error during sign up:", error);
      showError("Ocurrió un error durante el registro");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setEmail("");
    setPassword("");
    setConfirmPassword("");
    setGymCode("");
    setValidatedGym(null);
    setDni("");
    setShowConfirmModal(false);
  };

  const passwordsMatch = password.length >= 6 && password === confirmPassword;

  return (
    <>
      <form onSubmit={handleSubmit} className="space-y-5">
        <div className="space-y-2 animate-fade-in">
          <Label htmlFor="email" className="text-white font-medium">
            Correo electrónico
          </Label>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/60 z-10" />
            <Input
              id="email"
              type="email"
              value={email}
              onChange={handleEmailChange}
              placeholder="tu@email.com"
              required
              className="bg-black/50 text-white border-white/20 placeholder:text-white/60 pl-10 transition-all duration-200 focus:border-white/40"
            />
            {isCheckingEmail && (
              <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                <div className="animate-spin h-4 w-4 border-2 border-white/20 border-t-primary rounded-full"></div>
              </div>
            )}
          </div>
          {emailError && (
            <p className="text-red-400 text-sm mt-1">{emailError}</p>
          )}
          {emailChecked && !emailError && email && (
            <p className="text-green-400 text-sm mt-1">✓ Email disponible</p>
          )}
        </div>

        <div className="space-y-2 animate-fade-in" style={{ animationDelay: '0.1s' }}>
          <Label htmlFor="password" className="text-white font-medium">
            Contraseña
          </Label>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/60" />
            <Input
              id="password"
              type="password"
              value={password}
              onChange={handlePasswordChange}
              placeholder="Tu contraseña"
              required
              minLength={6}
              className="bg-black/50 text-white border-white/20 placeholder:text-white/60 pl-10 transition-all duration-200 focus:border-white/40"
            />
          </div>
        </div>

        <div className="space-y-2 animate-fade-in" style={{ animationDelay: '0.12s' }}>
          <Label htmlFor="confirmPassword" className="text-white font-medium">
            Confirmar contraseña
          </Label>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/60" />
            <Input
              id="confirmPassword"
              type="password"
              value={confirmPassword}
              onChange={handleConfirmPasswordChange}
              placeholder="Repetí tu contraseña"
              required
              minLength={6}
              className="bg-black/50 text-white border-white/20 placeholder:text-white/60 pl-10 transition-all duration-200 focus:border-white/40"
            />
          </div>
          {confirmPasswordError && (
            <p className="text-red-400 text-sm mt-1">{confirmPasswordError}</p>
          )}
          {!confirmPasswordError && passwordsMatch && (
            <p className="text-green-400 text-sm mt-1">✓ Las contraseñas coinciden</p>
          )}
        </div>

        <div className="animate-fade-in" style={{ animationDelay: '0.15s' }}>
          <GymCodeInput
            value={gymCode}
            onChange={setGymCode}
            onValidGym={setValidatedGym}
          />
        </div>

        {validatedGym && (
          <div className="space-y-2 animate-fade-in">
            <Label htmlFor="dni" className="text-white font-medium">
              DNI / Documento <span className="text-red-400">*</span>
            </Label>
            <div className="relative">
              <CreditCard className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/60 z-10" />
              <Input
                id="dni"
                type="text"
                value={dni}
                onChange={handleDniChange}
                placeholder="Tu número de documento"
                required
                maxLength={15}
                className="bg-black/50 text-white border-white/20 placeholder:text-white/60 pl-10 transition-all duration-200 focus:border-white/40"
              />
            </div>
            {dniError && (
              <p className="text-red-400 text-sm">{dniError}</p>
            )}
          </div>
        )}

        <Button
          type="submit"
          disabled={
            loading ||
            !!emailError ||
            isCheckingEmail ||
            !passwordsMatch ||
            (validatedGym && !dni)
          }
          className="w-full bg-primary hover:bg-primary/90 text-white font-semibold h-11 transition-all duration-200 hover:shadow-lg animate-fade-in"
          style={{ animationDelay: '0.2s' }}
        >
          {isCheckingEmail ? "Verificando email..." : "Registrarse"}
        </Button>

        <div className="flex items-center gap-4 py-3 animate-fade-in" style={{ animationDelay: '0.3s' }}>
          <div className="flex-1 h-px bg-white/20"></div>
          <span className="text-white/60 text-sm">o</span>
          <div className="flex-1 h-px bg-white/20"></div>
        </div>

        <div className="animate-fade-in" style={{ animationDelay: '0.4s' }}>
          <button
            type="button"
            onClick={onSwitchToSignIn}
            className="text-white font-medium text-sm hover:text-white/80 transition-all duration-200 bg-white/10 px-4 py-2.5 rounded-lg hover:bg-white/20 w-full"
          >
            ¿Ya tienes cuenta? Inicia sesión
          </button>
        </div>
      </form>

      <SignUpConfirmDialog
        open={showConfirmModal}
        onOpenChange={setShowConfirmModal}
        email={email}
        gymName={validatedGym?.name ?? null}
        dni={validatedGym ? dni : null}
        loading={loading}
        onConfirm={handleConfirmRegistration}
      />
    </>
  );
};
